// src/components/AudioSettings.jsx
"use client";

import { useState, useEffect } from "react";
import {
  RotateCcw,
  RotateCw,
  Gauge,
  Volume2,
  Settings2,
  RefreshCw,
  Check,
  Play,
  SkipBack,
} from "lucide-react";

// ── localStorage keys (shared with AudioPlaybackControls) ─────────────────
const LS_REWIND_KEY  = "audio_rewind_seconds";
const LS_FORWARD_KEY = "audio_forward_seconds";
const LS_SPEED_KEY   = "audio_default_speed";
const LS_VOLUME_KEY  = "audio_volume";

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const DEFAULTS = {
  rewindSec:  5,
  forwardSec: 5,
  speed:      1,
  volume:     1,
};

function getStored(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function broadcastChange() {
  window.dispatchEvent(new Event("audioPrefsChanged"));
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────────────────────

function SettingCard({ icon: Icon, title, description, children }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <p className="text-base font-semibold text-gray-900">{title}</p>
          <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function StepInput({ value, onChange, min = 1, max = 60, step = 1 }) {
  const decrement = () => onChange(Math.max(min, value - step));
  const increment = () => onChange(Math.min(max, value + step));

  return (
    <div className="flex items-center gap-0 rounded-xl border border-gray-200 overflow-hidden w-fit">
      <button
        onClick={decrement}
        disabled={value <= min}
        className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 transition-colors text-lg font-light select-none"
      >
        −
      </button>
      <div className="min-w-[52px] h-9 flex items-center justify-center border-x border-gray-200 bg-white">
        <span className="text-sm font-bold text-gray-900 tabular-nums">{value}s</span>
      </div>
      <button
        onClick={increment}
        disabled={value >= max}
        className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30 transition-colors text-lg font-light select-none"
      >
        +
      </button>
    </div>
  );
}

function PresetChips({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${
            value === opt
              ? "bg-gradient-to-r from-blue-600 to-teal-600 text-white border-blue-600 shadow-sm"
              : "bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600"
          }`}
        >
          {opt}s
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export function AudioSettings() {
  const [rewindSec, setRewindSecState]   = useState(() => getStored(LS_REWIND_KEY,  DEFAULTS.rewindSec));
  const [forwardSec, setForwardSecState] = useState(() => getStored(LS_FORWARD_KEY, DEFAULTS.forwardSec));
  const [speed, setSpeedState]           = useState(() => getStored(LS_SPEED_KEY,   DEFAULTS.speed));
  const [volume, setVolumeState]         = useState(() => getStored(LS_VOLUME_KEY,  DEFAULTS.volume));
  const [saved, setSaved]                = useState(false);

  // ── helpers ──────────────────────────────────────────────────────────────
  const setRewindSec = (v) => {
    setRewindSecState(v);
    try { localStorage.setItem(LS_REWIND_KEY, String(v)); } catch {}
    broadcastChange();
  };
  const setForwardSec = (v) => {
    setForwardSecState(v);
    try { localStorage.setItem(LS_FORWARD_KEY, String(v)); } catch {}
    broadcastChange();
  };
  const setSpeed = (v) => {
    setSpeedState(v);
    try { localStorage.setItem(LS_SPEED_KEY, String(v)); } catch {}
    broadcastChange();
  };
  const setVolume = (v) => {
    setVolumeState(v);
    try { localStorage.setItem(LS_VOLUME_KEY, String(v)); } catch {}
    broadcastChange();
  };

  const handleSave = () => {
    setSaved(true);
    broadcastChange();
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setRewindSec(DEFAULTS.rewindSec);
    setForwardSec(DEFAULTS.forwardSec);
    setSpeed(DEFAULTS.speed);
    setVolume(DEFAULTS.volume);
  };

  // ── preview helpers ───────────────────────────────────────────────────────
  const speedLabel = speed === 1 ? "Normal" : speed < 1 ? "Slow" : "Fast";

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-1">
          <Settings2 className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900">Audio Settings</h2>
        </div>
        <p className="text-base text-gray-500">
          Customise playback controls that appear on every recording in your session list.
        </p>
      </div>

      <div className="space-y-4">

        {/* ── Rewind ─────────────────────────────────────────────────────── */}
        <SettingCard
          icon={RotateCcw}
          title="Rewind duration"
          description="How many seconds the ← rewind button jumps back each time you press it."
        >
          <div className="flex flex-col gap-3">
            <PresetChips
              options={[3, 5, 10, 15, 30]}
              value={rewindSec}
              onChange={setRewindSec}
            />
            <div className="flex items-center gap-3">
              <StepInput value={rewindSec} onChange={setRewindSec} min={1} max={120} />
              <span className="text-xs text-gray-400">Custom (1–120 s)</span>
            </div>
          </div>
        </SettingCard>

        {/* ── Fast Forward ───────────────────────────────────────────────── */}
        <SettingCard
          icon={RotateCw}
          title="Fast-forward duration"
          description="How many seconds the → fast-forward button skips ahead each time you press it."
        >
          <div className="flex flex-col gap-3">
            <PresetChips
              options={[3, 5, 10, 15, 30]}
              value={forwardSec}
              onChange={setForwardSec}
            />
            <div className="flex items-center gap-3">
              <StepInput value={forwardSec} onChange={setForwardSec} min={1} max={120} />
              <span className="text-xs text-gray-400">Custom (1–120 s)</span>
            </div>
          </div>
        </SettingCard>

        {/* ── Playback Speed ─────────────────────────────────────────────── */}
        <SettingCard
          icon={Gauge}
          title="Default playback speed"
          description="Speed applied automatically when you start playing a recording. You can always change it inline per-session."
        >
          <div className="space-y-3">
            {/* Grid of speed chips */}
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
              {SPEED_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => setSpeed(s)}
                  className={`flex flex-col items-center py-2 rounded-xl border text-xs font-bold transition-all ${
                    speed === s
                      ? "bg-gradient-to-r from-blue-600 to-teal-600 text-white border-blue-600 shadow-sm scale-105"
                      : "bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600"
                  }`}
                >
                  <span>{s}x</span>
                  {s === 1 && (
                    <span className={`text-xs font-normal mt-0.5 ${speed === s ? "text-blue-100" : "text-gray-400"}`}>
                      Normal
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Slider for fine control */}
            <div className="flex items-center gap-3 pt-1">
              <span className="text-xs text-gray-400 w-8">0.5x</span>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.25"
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
                className="flex-1 h-1.5 accent-blue-600 cursor-pointer"
              />
              <span className="text-xs text-gray-400 w-8 text-right">2x</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
              <Gauge className="w-3.5 h-3.5 text-blue-600" />
              Current: <span className="font-semibold text-gray-800">{speed}x</span>
              <span className="text-gray-400">({speedLabel})</span>
            </div>
          </div>
        </SettingCard>

        {/* ── Default Volume ─────────────────────────────────────────────── */}
        <SettingCard
          icon={Volume2}
          title="Default volume"
          description="Starting volume level for all audio playback. You can adjust it inline per-session."
        >
          <div className="flex items-center gap-3">
            <Volume2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="flex-1 h-2 accent-blue-600 cursor-pointer"
            />
            <div className="min-w-[44px] text-right">
              <span className="text-sm font-bold text-gray-900 tabular-nums">
                {Math.round(volume * 100)}%
              </span>
            </div>
          </div>

          {/* Volume level label */}
          <div className="mt-2 flex gap-2 justify-between text-xs text-gray-300 px-6">
            {["Mute", "25%", "50%", "75%", "Max"].map((l) => (
              <span key={l}>{l}</span>
            ))}
          </div>
        </SettingCard>

        {/* ── Preview ────────────────────────────────────────────────────── */}
        <div className="bg-gradient-to-br from-blue-50 to-teal-50 border border-blue-100 rounded-2xl p-5">
          <p className="text-xs font-semibold text-blue-600 mb-3 uppercase tracking-wider">
            Preview – your controls will look like this
          </p>
          
          {/* Mocking the compact controls exactly as they appear in the sidebar */}
          <div className="flex items-center justify-between gap-1 w-full bg-white rounded-xl p-3 shadow-sm border border-gray-100">
            
            {/* Rewind */}
            <div className="flex flex-col items-center justify-center flex-1 text-gray-500">
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="text-xs leading-none mt-0.5">{rewindSec}s</span>
            </div>

            {/* Play */}
            <div className="flex flex-col items-center justify-center flex-1 text-blue-600">
              <Play className="w-3.5 h-3.5" />
              <span className="text-xs leading-none mt-0.5 font-medium">Play</span>
            </div>

            {/* Fast Forward */}
            <div className="flex flex-col items-center justify-center flex-1 text-gray-500">
              <RotateCw className="w-3.5 h-3.5" />
              <span className="text-xs leading-none mt-0.5">{forwardSec}s</span>
            </div>

            {/* Restart */}
            <div className="flex flex-col items-center justify-center flex-1 text-gray-500">
              <SkipBack className="w-3.5 h-3.5" />
              <span className="text-xs leading-none mt-0.5">Start</span>
            </div>

            {/* Mute */}
            <div className="flex flex-col items-center justify-center flex-1 text-gray-500">
              <Volume2 className="w-3.5 h-3.5" />
              <span className="text-xs leading-none mt-0.5">Mute</span>
            </div>

          </div>
          
          <div className="mt-3 text-center text-xs text-gray-500">
            Playback Speed: <span className="font-semibold text-gray-800">{speed}x</span> · Volume: <span className="font-semibold text-gray-800">{Math.round(volume * 100)}%</span>
          </div>
        </div>

        {/* ── Actions ────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm ${
              saved
                ? "bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-teal-400/30"
                : "bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:shadow-lg shadow-blue-200/50 hover:shadow-blue-300/50"
            }`}
          >
            {saved ? (
              <>
                <Check className="w-4 h-4" /> Saved!
              </>
            ) : (
              "Save preferences"
            )}
          </button>

          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:text-gray-900 bg-white border border-gray-200 hover:border-gray-300 transition-all"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Reset to defaults
          </button>
        </div>
      </div>
    </div>
  );
}